if (!customElements.get('oahu-brand-statement')) {
  customElements.define(
    'oahu-brand-statement',
    class OahuBrandStatement extends HTMLElement {
      connectedCallback() {
        this.logo = this.querySelector('[data-parallax-logo]');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.mobileMq = window.matchMedia('(max-width: 620px)');
        this.smoothing = parseFloat(this.dataset.logoSmoothness || '0.08');
        this.rafId = null;

        this.applyLogoConfig = this.applyLogoConfig.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);

        this.applyLogoConfig();
        this.currentY = this.riseDistance;
        this.targetY = this.riseDistance;
        this.setupReveal();

        if (!this.reducedMotion && this.logo) {
          this.startAnimationLoop();
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.mobileMq.addEventListener('change', this.onBreakpointChange);
          this.updateTargetFromScroll();
        } else if (this.logo) {
          this.setLogoPosition(0);
          this.classList.add('is-logo-settled');
        }
      }

      disconnectedCallback() {
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
        }

        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }

        if (this.mobileMq) {
          this.mobileMq.removeEventListener('change', this.onBreakpointChange);
        }

        if (this.revealObserver) {
          this.revealObserver.disconnect();
        }
      }

      applyLogoConfig() {
        const isMobile = this.mobileMq.matches;

        this.riseDistance = parseFloat(
          isMobile
            ? this.dataset.logoRiseDistanceMobile || this.dataset.logoRiseDistance || '28'
            : this.dataset.logoRiseDistance || '28'
        );

        this.scrollRange = parseFloat(
          isMobile
            ? this.dataset.logoScrollRangeMobile || this.dataset.logoScrollRange || '2.2'
            : this.dataset.logoScrollRange || '2.2'
        );
      }

      onBreakpointChange() {
        this.applyLogoConfig();
        this.classList.remove('is-logo-settled');
        this.updateTargetFromScroll();
        this.currentY = this.targetY;
      }

      setupReveal() {
        const threshold = parseFloat(this.dataset.revealThreshold || '0.15');

        this.revealObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.classList.add('is-visible');
                this.revealObserver.disconnect();
              }
            });
          },
          {
            threshold,
            rootMargin: '0px 0px -5% 0px',
          }
        );

        this.revealObserver.observe(this);
      }

      onScroll() {
        this.updateTargetFromScroll();
      }

      startAnimationLoop() {
        const animate = () => {
          const delta = this.targetY - this.currentY;

          if (Math.abs(delta) > 0.02) {
            this.currentY += delta * this.smoothing;
          } else {
            this.currentY = this.targetY;
          }

          this.setLogoPosition(this.currentY);

          if (this.targetY <= 0.05) {
            this.classList.add('is-logo-settled');
          }

          this.rafId = requestAnimationFrame(animate);
        };

        this.rafId = requestAnimationFrame(animate);
      }

      easeOutQuint(value) {
        return 1 - Math.pow(1 - value, 5);
      }

      setLogoPosition(translateVh) {
        this.logo.style.setProperty('--obs-logo-y', `${translateVh}vh`);
      }

      updateTargetFromScroll() {
        const rect = this.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const sectionHeight = Math.max(rect.height, viewportHeight * 0.5);
        const totalRange = viewportHeight + sectionHeight * this.scrollRange;
        const rawProgress = (viewportHeight - rect.top) / totalRange;
        const progress = Math.min(1, Math.max(0, rawProgress));
        const eased = this.easeOutQuint(progress);

        this.targetY = this.riseDistance * (1 - eased);
      }
    }
  );
}
